/* eslint-disable no-console */
import type { LiveUpdateMessage, OnPdfFileChangedMessage } from '@evidia/shared';
import { PdfFileStatus } from '@evidia/shared';
import { notification } from 'antd';
import { useEffect } from 'react';
import { connect } from 'socket.io-client';
import { BASE_WS } from '../const/app';
import { useDownloadFile } from '../hooks/useDownloadFile';
import { useGroupSettings } from '../hooks/useGroupSettings';
import { useNotifications } from '../hooks/useNotifications';
import { useRefetchUser } from '../hooks/useRefetchUser';
import { useFetchInvites } from '../stores/SchoolInvitesStore';
import { useSocketStore } from '../stores/SocketStore';
import { useSystemStore } from '../stores/SystemStore';

const Push: React.FC = () => {
	const { refetch: groupSettingsRefetch } = useGroupSettings();
	const { addNewNotification, onNotificationRead } = useNotifications();
	const refetchUser = useRefetchUser();
	const token = useSystemStore(s => s.token);
	const removeClientCode = useSocketStore(s => s.removeClientCode);
	const hasClientCode = useSocketStore(s => s.hasClientCode);
	const downloadFile = useDownloadFile();
	const { refetch: refetchInvites } = useFetchInvites();

	const OnPdfFileChanged = async (msgParsed: OnPdfFileChangedMessage) => {
		if (msgParsed.status === PdfFileStatus.DONE) {
			if (!hasClientCode(msgParsed.clientCode)) {
				return;
			}
			removeClientCode(msgParsed.clientCode);
			notification.destroy(msgParsed.clientCode);
			if (msgParsed.fileUrl) {
				await downloadFile(msgParsed.fileUrl, `${msgParsed.type.toLocaleLowerCase()}-${msgParsed.id}.pdf`);
			}
		}

		if (msgParsed.status === PdfFileStatus.ERROR) {
			if (!hasClientCode(msgParsed.clientCode)) {
				return;
			}
			removeClientCode(msgParsed.clientCode);
			notification.error({
				message: 'Chyba při generování PDF',
				description: 'Něco se nepovedlo, zkuste to prosím znovu.',
			});
		}
	};

	const getMessageWithoutKey = (msg: LiveUpdateMessage) => {
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		const { key, ...msgWithoutKey } = msg;
		return msgWithoutKey;
	};

	useEffect(() => {
		const socket = connect(BASE_WS, {
			transports: ['websocket', 'polling', 'flashsocket'],
			auth: {
				token,
			},
		});

		socket.on('connect', () => {
			console.log('Socket connected');
		});

		socket.on('live-update', async (data: string) => {
			const msgParsed = JSON.parse(data) as LiveUpdateMessage;
			const { functions } = useSocketStore.getState();

			const msgWithoutKey = getMessageWithoutKey(msgParsed);

			functions[msgParsed.key]?.forEach(fn => fn.fn(msgWithoutKey));

			console.log('Received message:', msgParsed.key, msgWithoutKey);

			switch (msgParsed.key) {
				case 'OnPdfFileChanged':
					await OnPdfFileChanged(msgParsed);
					break;
				case 'SchoolInviteRemovedMessage':
					notification.info({
						message: 'Byl jste odstraněn ze školy',
					});
					refetchUser();
					refetchInvites();
					break;
				case 'SchoolInviteCreatedMessage':
					refetchInvites();
					break;
				case 'OnNewStudentMessage':
					groupSettingsRefetch();
					break;
				case 'NotificationMessage':
					addNewNotification({
						id: msgParsed.id,
						type: msgParsed.type,
					});
					break;
				case 'NotificationReadMessage':
					onNotificationRead(msgParsed.id);
					break;
				case 'GroupBlockedMessage':
					refetchUser(); // todo
					break;
				case 'TierChangedMessage':
					refetchUser();
					break;
				case 'TeachingLessonRecordMessage':
					break;
				case 'DrivingLessonRecordMessage':
					break;
				default:
					break;
			}
		});

		return () => {
			socket.disconnect();
		};
	}, []);

	return null;
};
export default Push;
